<template>
  <div class="gs-app gs-billing-page">
    <div class="gs-billing-page__content">
      <v-wait for="purchases">
        <header class="gs-billing-page__header gs-wizard-card">
          <div class="gs-billing-page__eyebrow">Account billing</div>
          <h1>{{ $t('frontend_groovesell_billing_setting') }}</h1>
          <p>{{ $t('frontend_groovesell_billing_sett_conf') }}</p>
        </header>
        <section class="gs-billing-page__workspace gs-wizard-card">
          <div class="gs-billing-page__tabs">
            <gd-tabs :tabs="tabs" :current-tab="currentTab" :wrapper-class="'pt-4 px-0 gs-default-tabs'"
              :tab-class="'pb-4 mr-4 gs-default-tabs-item'"
              :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
              :line-class="'gs-default-tabs-active-line'" @onClick="handleTabChange" />
          </div>
          <div class="gs-billing-page__panel">
            <div v-if="currentTab === 'info'">
              <info ref="info" :access-level="accessLevel" :next-billing-date="nextBillingDate" />
            </div>

            <div v-else-if="currentTab === 'transactions'">
              <transactions ref="transactions" :purchases="purchases" @refresh="loadPurchases" />
            </div>
          </div>
        </section>
      </v-wait>
    </div>
    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<style scoped>
.gs-billing-page {
  min-height: 100%;
  padding: 32px 28px 56px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gs-billing-page__content {
  width: 100%;
  max-width: 1260px;
  margin: 0 auto;
}

.gs-billing-page__header,
.gs-billing-page__workspace {
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gs-billing-page__header {
  margin-bottom: 18px;
  padding: 20px 24px;
}

.gs-billing-page__eyebrow {
  margin-bottom: 6px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  line-height: 1.4;
  text-transform: uppercase;
}

.gs-billing-page__header h1 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.gs-billing-page__header p {
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-billing-page__workspace {
  padding: 0 24px 24px;
}

.gs-billing-page__tabs {
  padding: 0 0 2px;
  border-bottom: 1px solid var(--gs-border);
}

.gs-billing-page__panel {
  min-width: 0;
  padding-top: 18px;
}

@media (max-width: 640px) {
  .gs-billing-page {
    padding: 18px 12px 32px;
  }

  .gs-billing-page__header,
  .gs-billing-page__workspace {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>

<script>
import axios from 'axios';
import transactions from './Tabs/Transactions';
import info from './Tabs/Info';

const touchMap = new WeakMap();

export default {
  middleware: 'auth',
  layout: 'default',
  components: {
    info,
    transactions,
  },
  data() {
    return {
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: '',
      },
      tabs: [
        {
          title: `<span class="text-sm">${this.$t('frontend_info')}<span>`,
          value: 'info'
        },
        {
          title: `<span class="text-sm">${this.$t('frontend_transactions')}<span>`,
          value: 'transactions'
        }
      ],
      currentTab: 'info',
      accessLevel: null,
      nextBillingDate: null,
      purchases: [],
      domLayout: 'autoHeight',
      complete: true,
    };
  },
  async created() {
    this.$wait.start('purchases');
    this.accessLevel = 'Free User';
    if (this.$store.getters.isPlatinumUser) {
      this.accessLevel = 'Platinum Lifetime User';
    } else if (this.$store.getters.isGroovePagesUser) {
      this.accessLevel = this.whitelabelAppData('pages').title + ' User';
    }
    await this.loadPurchases(false);
    this.$wait.end('purchases');
  },
  methods: {
    async loadPurchases(showLoader = true) {
      try {
        if (showLoader) this.$loader.start('loadPurchases');
        const { data } = await axios.get('/purchases');
        if (data.status == 'success') {
          this.purchases = data.purchases;
          this.$loader.end('loadPurchases');
        } else {
          this.alertProps = {
            show: true,
            variant: 'error',
            title: this.$t('frontend_error'),
            message: this.$t('frontend_analytics_billing_unable_load_pur'),
          };
        }
      } catch (e) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: this.$t('frontend_error'),
          message: this.$t('frontend_analytics_billing_unable_load_pur'),
        };
      }

      const nextBillingDateRequest = await axios.get('/get-next-transaction-date');
      const nextBillingData = nextBillingDateRequest.data;
      this.nextBillingDate = nextBillingData.status == 'success' ? nextBillingData.next_transaction_date : null;
      this.$loader.end('loadPurchases');
    },
    handleTabChange(newTab) {
      this.currentTab = newTab;
    },
  }
};
</script>
